import { ContestStatus } from '@betterpool/api-types/contests-service';
import type { UserContest } from '@betterpool/api-types/contests-service';

import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';

import type { ContestCardActionBanner } from '../types';
import { getIsEOContest } from '../../../utils/getIsEOContest';

import UserContestMetaSurvivor from './UserContestMetaSurvivor';
import ContestMetaGeneric from './ContestMetaGeneric';
import UserContestMetaGeneric from './UserContestMetaGeneric';
import ContestMetaGenericCommissioner from './ContestMetaCommissioner';

type ContestCardFooterProps = {
  contest: Contest | MyUserContest;
  isMyContestsView: boolean;
  isSurvivor: boolean;
  winning: number;
  entryPrice: number;
  actionBanner?: ContestCardActionBanner;
  isSettled: boolean;
  totalEntries: number;
  winningEntries: number;
  userAliveEntries: number;
  userSegmentedPayouts?: number;
  isEntertainmentOnly?: boolean;
  rank?: number;
  survivor?: UserContest['survivor'];
  isCommissioner: boolean;
};

function ContestCardFooter({
  contest,
  totalEntries,
  winningEntries,
  winning,
  entryPrice,
  actionBanner,
  isSettled,
  isMyContestsView,
  isSurvivor,
  userAliveEntries,
  userSegmentedPayouts,
  isEntertainmentOnly,
  rank,
  survivor,
  isCommissioner,
}: ContestCardFooterProps) {
  // v2-parsed contests carry entitySource, not source; prefer it (matches getContestNavigationTarget).
  const isEOContest = getIsEOContest(
    (contest as { entitySource?: string }).entitySource ?? contest.source
  );

  if (isCommissioner && isMyContestsView) {
    return (
      <ContestMetaGenericCommissioner
        actionBanner={actionBanner}
        isEntertainmentOnly={isEntertainmentOnly || isEOContest}
        contest={contest}
        survivor={survivor}
        isSurvivor={isSurvivor}
      />
    );
  }

  if (contest.status !== ContestStatus.SCHEDULED && isMyContestsView && isSurvivor)
    return (
      <UserContestMetaSurvivor
        isSettled={isSettled}
        numberOfUserEntries={contest.entries.user}
        winning={winning}
        entryPrice={entryPrice}
        userAliveEntries={userAliveEntries}
        winningEntries={winningEntries}
        totalEntries={totalEntries}
        actionBanner={actionBanner}
        isEntertainmentOnly={isEntertainmentOnly || isEOContest}
      />
    );

  if (contest.status !== ContestStatus.SCHEDULED && isMyContestsView && !isSurvivor)
    return (
      <UserContestMetaGeneric
        isSettled={isSettled}
        numberOfUserEntries={contest.entries.user}
        winning={winning}
        entryPrice={entryPrice}
        actionBanner={actionBanner}
        userSegmentedPayouts={userSegmentedPayouts}
        isEntertainmentOnly={isEntertainmentOnly || isEOContest}
        rank={rank}
        totalEntries={totalEntries}
      />
    );

  return (
    <ContestMetaGeneric
      actionBanner={actionBanner}
      contest={contest}
      isEntertainmentOnly={isEntertainmentOnly || isEOContest}
    />
  );
}

export default ContestCardFooter;
